/**
 * The Reliability page's feed, straight from pv-auth:
 *   GET /provider/admin/reliability?since=7d[&refresh=1]   (SUPER_ADMIN)
 * Shapes mirror src/services/reliability/ in pv-auth-service, which is
 * command-center tools/error-budget (budget.py) computed live.
 *
 * Every field is optional and read through normalizeReliability, so an older
 * or newer server can never blank the page. A null is "not measured" or "not
 * enough data" with its reason and renders as the words, never as 0.
 */
import { NOT_MEASURED } from './format';

export type ReliabilityStatus = 'within' | 'over' | 'not enough data' | 'not measured';
export type FailureKind = 'trace' | 'daily_check' | 'first_check' | 'help_request' | 'feedback' | 'sign_in';

export interface FailureRow { kind: FailureKind | string; id: string; userId: string | null; peerId: string | null; code: string; at: string | null }
export interface Indicator {
  key: string; label: string;
  objective: number | null; max: number | null; floor: number | null;
  n: number | null; good: number | null; bad: number | null;
  observed: number | null; badShare: number | null; budget: number | null; burn: number | null;
  status: ReliabilityStatus; reason: string | null;
  failures: FailureRow[]; failuresTotal: number;
}
export interface Surface {
  surface: string; label: string; note: string | null;
  worstBurn: number | null; worstIndicator: string | null;
  status: ReliabilityStatus; indicators: Indicator[];
}
/**
 * Sign-ins kept, per app version (pv-auth SignInFollowUp, ERROR_PATHS_AUDIT_2026-10-25):
 * a sign-in answered 200 with a token, then a 401 for the same client within
 * the follow-up window, is a client that never stored its token.
 */
export interface SignInVersion {
  surface: string; appVersion: string;
  signIns: number; followedBy401: number; missingToken: number;
  /** Null under five sign-ins: never a share from a handful. */
  keptShare: number | null;
}
export interface SignIns { followUpSeconds: number | null; measured: boolean; reason: string | null; byVersion: SignInVersion[] }

export interface Reliability {
  generatedAt: string | null; fetchedAt: string | null; cacheSeconds: number | null;
  window: { since: string | null; until: string | null; days: number | null; label: string };
  targets: { source: string; status: string };
  freezeRule: string;
  sentry: { measured: boolean; reason: string | null };
  accounts: { members: number | null; teamExcluded: number | null };
  unattributed: { traces: number; dailyChecks: number; firstChecks: number; forms: number; signIns: number };
  overBudget: number;
  surfaces: Surface[];
  /** Absent on a server older than pv-auth #483: measured false with that reason. */
  signIns: SignIns;
}

export const TARGETS_FILE = 'tools/error-budget/slo.json';
export const TARGETS_URL = 'https://github.com/Posture-Vision/command-center/blob/main/tools/error-budget/slo.json';
export const FREEZE_RULE = 'Over 1.0 only fixes merge on that surface.';
const STATUSES: ReliabilityStatus[] = ['within', 'over', 'not enough data', 'not measured'];

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const n = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
const count = (v: unknown): number => n(v) ?? 0;
const status = (v: unknown): ReliabilityStatus => (STATUSES.includes(v as ReliabilityStatus) ? (v as ReliabilityStatus) : 'not measured');

const failureOf = (raw: unknown): FailureRow | null => {
  const f = obj(raw);
  const id = str(f.id);
  if (!id) return null;
  return { kind: str(f.kind) ?? 'unknown', id, userId: str(f.userId), peerId: str(f.peerId), code: str(f.code) ?? 'not recorded', at: str(f.at) };
};

const indicatorOf = (raw: unknown): Indicator => {
  const i = obj(raw);
  const failures = (Array.isArray(i.failures) ? i.failures : []).map(failureOf).filter((f): f is FailureRow => f !== null);
  return {
    key: str(i.key) ?? 'unknown',
    label: str(i.label) ?? str(i.key) ?? 'Unnamed indicator',
    objective: n(i.objective), max: n(i.max), floor: n(i.floor),
    n: n(i.n), good: n(i.good), bad: n(i.bad),
    observed: n(i.observed), badShare: n(i.badShare), budget: n(i.budget), burn: n(i.burn),
    status: status(i.status), reason: str(i.reason),
    failures, failuresTotal: n(i.failuresTotal) ?? failures.length,
  };
};

const surfaceOf = (raw: unknown): Surface => {
  const s = obj(raw);
  return {
    surface: str(s.surface) ?? 'unknown',
    label: str(s.label) ?? str(s.surface) ?? 'Unnamed surface',
    note: str(s.note),
    worstBurn: n(s.worstBurn),
    worstIndicator: str(s.worstIndicator),
    status: status(s.status ?? 'not enough data'),
    indicators: (Array.isArray(s.indicators) ? s.indicators : []).map(indicatorOf),
  };
};

const signInVersionOf = (raw: unknown): SignInVersion | null => {
  const v = obj(raw);
  const appVersion = str(v.appVersion);
  if (!appVersion) return null;
  return {
    surface: str(v.surface) ?? 'unknown', appVersion,
    signIns: count(v.signIns), followedBy401: count(v.followedBy401), missingToken: count(v.missingToken),
    keptShare: n(v.keptShare),
  };
};

const signInsOf = (raw: unknown): SignIns => {
  if (!raw || typeof raw !== 'object') return { followUpSeconds: null, measured: false, reason: 'this server does not report sign-ins yet', byVersion: [] };
  const si = obj(raw);
  return {
    followUpSeconds: n(si.followUpSeconds),
    measured: si.measured === true,
    reason: str(si.reason),
    byVersion: (Array.isArray(si.byVersion) ? si.byVersion : []).map(signInVersionOf).filter((v): v is SignInVersion => v !== null),
  };
};

/** Any server answer, made safe to render. */
export const normalizeReliability = (raw: unknown): Reliability => {
  const r = obj(raw);
  const w = obj(r.window);
  const t = obj(r.targets);
  const se = obj(r.sentry);
  const a = obj(r.accounts);
  const u = obj(r.unattributed);
  const surfaces = (Array.isArray(r.surfaces) ? r.surfaces : []).map(surfaceOf);
  return {
    generatedAt: str(r.generatedAt),
    fetchedAt: str(r.fetchedAt) ?? str(r.generatedAt),
    cacheSeconds: n(r.cacheSeconds),
    window: { since: str(w.since), until: str(w.until), days: n(w.days), label: str(w.label) ?? '7d' },
    targets: { source: str(t.source) ?? TARGETS_FILE, status: str(t.status) ?? 'proposed, awaiting Brian' },
    freezeRule: str(r.freezeRule) ?? FREEZE_RULE,
    sentry: { measured: se.measured === true, reason: str(se.reason) },
    accounts: { members: n(a.members), teamExcluded: n(a.teamExcluded) },
    unattributed: { traces: count(u.traces), dailyChecks: count(u.dailyChecks), firstChecks: count(u.firstChecks), forms: count(u.forms), signIns: count(u.signIns) },
    overBudget: n(r.overBudget) ?? surfaces.filter((s) => s.status === 'over').length,
    surfaces,
    signIns: signInsOf(r.signIns),
  };
};

export async function fetchReliability(refresh = false, since = '7d'): Promise<Reliability> {
  const { apiGet } = await import('@/services/base');
  return normalizeReliability(await apiGet(`/provider/admin/reliability?since=${encodeURIComponent(since)}${refresh ? '&refresh=1' : ''}`, true));
}

/* ── words ──────────────────────────────────────────────────────────────── */

export const STATUS_LABEL: Record<ReliabilityStatus, string> = {
  within: 'Within budget',
  over: 'Over budget',
  'not enough data': 'Not enough data',
  'not measured': 'Not measured',
};

export const pct = (v: number | null, digits = 1): string => {
  if (v === null) return NOT_MEASURED;
  const p = v * 100;
  return `${Number.isInteger(p) ? p : p.toFixed(digits)}%`;
};

export const burnLabel = (b: number | null): string => (b === null ? 'no burn yet' : b.toFixed(2));

/** The target as a sentence: "97% good" for a share, "at most 200" for a count. */
export const targetLabel = (i: Indicator): string => {
  if (i.max !== null) return `at most ${i.max.toLocaleString()}`;
  if (i.objective !== null) return `${pct(i.objective)} good`;
  return 'no target';
};

/** What was observed: the good share, the count, or the reason there is none. */
export const observedLabel = (i: Indicator): string => {
  if (i.observed === null) return i.status === 'not measured' ? NOT_MEASURED : 'not enough data';
  if (i.max !== null) return `${i.observed.toLocaleString()} events`;
  return pct(i.observed);
};

export const sampleLabel = (i: Indicator): string => {
  if (i.n === null) return NOT_MEASURED;
  if (i.max !== null) return `${i.n.toLocaleString()} events`;
  const floor = i.floor !== null && i.floor > 0 ? ` (needs ${i.floor})` : '';
  return `${i.n.toLocaleString()}${floor}`;
};

/** The bar's width on a 0 to 2 scale, so 1.0 sits at the middle line; null draws no bar. */
export const barWidth = (b: number | null): number | null => (b === null ? null : Math.max(0, Math.min(b, 2)) * 50);

const KIND_LABEL: Record<string, string> = {
  trace: 'engine session', daily_check: 'daily check', first_check: 'first check', help_request: 'help request', feedback: 'feedback', sign_in: 'sign-in',
};
export const kindLabel = (k: string): string => KIND_LABEL[k] ?? k.replace(/_/g, ' ');

/** The autopsy page for a trace whose ids allow it; a sign-in opens the person; every other row opens the failed-sessions page for the same window. */
export const failureHref = (f: FailureRow, windowLabel = '7d'): string =>
  f.kind === 'trace' && f.userId && f.peerId
    ? `/people/${encodeURIComponent(f.userId)}/sessions/${encodeURIComponent(f.peerId)}`
    : f.kind === 'sign_in' && f.userId
      ? `/people/${encodeURIComponent(f.userId)}`
      : `/sessions/failed?since=${encodeURIComponent(windowLabel)}`;

/** "4 of 4 followed by a 401 (4 with no token)", or "kept" when none was. */
export const signInLine = (v: SignInVersion): string =>
  v.followedBy401 === 0 ? 'every one kept' : `${v.followedBy401} of ${v.signIns} followed by a 401${v.missingToken ? ` (${v.missingToken} with no token sent)` : ''}`;

/** The nav badge: surfaces over budget, or null while unknown (no badge, never a 0 that means "unread"). */
export const overBudgetBadge = (r: Reliability | null | undefined): number | null => (r ? r.surfaces.filter((s) => s.status === 'over').length : null);
